import * as THREE from "three";
import { IVfxTask } from "../../../core/runtime/vfx_task_runner.js";
import { Vfx3DRuntime } from "../../../vfx_3d_runtime.js";
import { IController, StopBehavior } from "../../../core/types/controller_types.js";
import { VfxRadialStreakBatch } from "../../../core/batches/vfx_radial_streak_batch.js";
import { VfxBallisticStreakBatch } from "../../../core/batches/vfx_ballistic_streak_batch.js";
import { ResourceID } from "../../../../resource_loader/resource_id.js";
import { randomRange } from "../../../../common/common_funcs.js";
import { VfxGradientAtlas } from "../../../core/textures/vfx_gradient_atlas.js";
import { SceneManager } from "../../../../core/scene_manager.js";

export class VfxFireworkExplosionTask implements IVfxTask, IController
{
  // --- 2D Sparks 1 Constants (VfxRadialStreakBatch) ---
  private static readonly SPARKS_1_BURST_COUNT             : number      = 6;
  private static readonly SPARKS_1_LIFESPAN                : number      = 0.4;
  private static readonly SPARKS_1_SPEED                   : number      = 2;
  private static readonly SPARKS_1_MIN_SPAWN_AREA_RADIUS   : number      = 0.2;
  private static readonly SPARKS_1_MAX_SPAWN_AREA_RADIUS   : number      = 0.4;
  private static readonly SPARKS_1_MIN_WIDTH               : number      = 1.2;
  private static readonly SPARKS_1_MAX_WIDTH               : number      = 2;
  private static readonly SPARKS_1_MIN_HEIGHT              : number      = 1;
  private static readonly SPARKS_1_MAX_HEIGHT              : number      = 1.5;
  private static readonly SPARKS_1_BRIGHTNESS_FADE_START_PROGRESS: number = 0.4; // Момент часу (0.0..1.0), коли починається спад яскравості
  private static readonly SPARKS_1_DISSOLVE_START_PROGRESS : number      = 0.05; // Момент часу (0.0..1.0), коли частинки починають розчинятися через CloudNoise
  private static readonly SPARKS_1_DISSOLVE_DIRECTION_WEIGHT: number     = 0.4; // Баланс розчинення між шумом (0.0) і напрямком уздовж частинки (1.0)
  private static readonly SPARKS_1_DISSOLVE_SOFTNESS       : number      = 0.1; // М'якість границі розчинення
  private static readonly SPARKS_1_COLOR_1                 : THREE.Color = new THREE.Color( 0xFFFFA0 ); // Ядро (гарячий біло-жовтий)
  private static readonly SPARKS_1_COLOR_2                 : THREE.Color = new THREE.Color( 0xFFA03C ); // Середина (бурштиновий)

  // --- 3D Sparks 2 Constants (VfxBallisticStreakBatch) ---
  private static readonly SPARKS_2_BURST_COUNT             : number      = 20;
  private static readonly SPARKS_2_LIFESPAN                : number      = 0.45;
  private static readonly SPARKS_2_COLOR_1                 : THREE.Color = new THREE.Color( 0xFFFFC8 );
  private static readonly SPARKS_2_COLOR_2                 : THREE.Color = new THREE.Color( 0xFFB450 );
  private static readonly SPARKS_2_COLOR_3                 : THREE.Color = new THREE.Color( 0xFF5014 );
  private static readonly SPARKS_2_COLOR_2_PROGRESS        : number      = 0.5;
  private static readonly SPARKS_2_COLOR_3_PROGRESS        : number      = 0.7;
  private static readonly SPARKS_2_MIN_SPEED               : number      = 5;
  private static readonly SPARKS_2_MAX_SPEED               : number      = 8;
  private static readonly SPARKS_2_UP_MIN                  : number      = 5;
  private static readonly SPARKS_2_UP_MAX                  : number      = 7;
  private static readonly SPARKS_2_GRAVITY                 : number      = 24;
  private static readonly SPARKS_2_MIN_WIDTH               : number      = 0.56;
  private static readonly SPARKS_2_MAX_WIDTH               : number      = 0.62;
  private static readonly SPARKS_2_MIN_HEIGHT              : number      = 0.66;
  private static readonly SPARKS_2_MAX_HEIGHT              : number      = 0.72;
  private static readonly SPARKS_2_STRETCH_FACTOR          : number      = 0.05;
  private static readonly SPARKS_2_SHRINK_START_RATIO      : number      = 0.6;
  private static readonly SPARKS_2_SPAWN_AREA_RADIUS       : number      = 0.6;

  // --- 2D Sparks 3 Constants (VfxRadialStreakBatch + SparkGlow4) ---
  private static readonly SPARKS_3_BURST_COUNT             : number      = 8;
  private static readonly SPARKS_3_LIFESPAN                : number      = 0.35;
  private static readonly SPARKS_3_SPEED                   : number      = 6;
  private static readonly SPARKS_3_MIN_SPAWN_AREA_RADIUS   : number      = 0;
  private static readonly SPARKS_3_MAX_SPAWN_AREA_RADIUS   : number      = 0.2;
  private static readonly SPARKS_3_MIN_WIDTH               : number      = 0.5;
  private static readonly SPARKS_3_MAX_WIDTH               : number      = 1;
  private static readonly SPARKS_3_MIN_HEIGHT              : number      = 1.5;
  private static readonly SPARKS_3_MAX_HEIGHT              : number      = 2;
  private static readonly SPARKS_3_BRIGHTNESS_FADE_START_PROGRESS: number = 0.3; // Момент часу (0.0..1.0), коли починається спад яскравості
  private static readonly SPARKS_3_DISSOLVE_START_PROGRESS : number      = 0.1; // Момент часу (0.0..1.0), коли частинки починають розчинятися через CloudNoise
  private static readonly SPARKS_3_DISSOLVE_DIRECTION_WEIGHT: number     = 0.5; // Баланс розчинення між шумом (0.0) і напрямком уздовж частинки (1.0)
  private static readonly SPARKS_3_DISSOLVE_SOFTNESS       : number      = 0.15; // М'якість границі розчинення
  private static readonly SPARKS_3_COLOR_1                 : THREE.Color = new THREE.Color( 0xFFFFC8 ); // Ядро (гарячий біло-жовтий)
  private static readonly SPARKS_3_COLOR_2                 : THREE.Color = new THREE.Color( 0xFFA03C ); // Середина (бурштиновий)
  private static readonly SPARKS_3_COLOR_3                 : THREE.Color = new THREE.Color( 0xFF5014 ); // Кромка/хвости (глибокий червоно-помаранчевий)

  private static readonly TOTAL_LIFESPAN: number = Math.max(
    VfxFireworkExplosionTask.SPARKS_1_LIFESPAN,
    VfxFireworkExplosionTask.SPARKS_2_LIFESPAN,
    VfxFireworkExplosionTask.SPARKS_3_LIFESPAN
  );
  private static readonly PI2: number = Math.PI * 2.0;

  private static sparks_1_gradient_id: number = -1;
  private static sparks_2_gradient_id: number = -1;
  private static sparks_3_gradient_id: number = -1;

  public is_active       : boolean = true;
  private position       : THREE.Vector3 = new THREE.Vector3();
  private start_time     : number;
  private has_spawned    : boolean = false;

  public constructor( position: THREE.Vector3 )
  {
    this.position.copy( position );
    const camera_pos = SceneManager.camera?.position ?? new THREE.Vector3( 0, 3.7, 13 );
    const to_camera = new THREE.Vector3().subVectors( camera_pos, this.position ).normalize();
    this.position.addScaledVector( to_camera, 0.35 );
    this.start_time = Vfx3DRuntime.current_time;
  }

  public setPosition( pos: THREE.Vector3 ): void
  {
    this.position.copy( pos );
    const camera_pos = SceneManager.camera?.position ?? new THREE.Vector3( 0, 3.7, 13 );
    const to_camera = new THREE.Vector3().subVectors( camera_pos, this.position ).normalize();
    this.position.addScaledVector( to_camera, 0.35 );
  }

  public stop( _behavior?: StopBehavior ): void
  {
    this.is_active = false;
  }

  public start(): void
  {
    this.is_active = true;
  }

  public accelerate( _factor: number ): void {}

  private static readonly scratch_pos: THREE.Vector3 = new THREE.Vector3();
  private static readonly scratch_vel: THREE.Vector3 = new THREE.Vector3();
  private static readonly scratch_size: THREE.Vector2 = new THREE.Vector2();

  public onUpdate( now: number ): boolean
  {
    if ( !this.is_active )
      return true;

    if ( now < this.start_time )
      return false;

    const age = now - this.start_time;

    if ( !this.has_spawned )
    {
      this.has_spawned = true;
      this.spawnSparks1();
      this.spawnSparks2();
      this.spawnSparks3();
    }

    if ( age >= VfxFireworkExplosionTask.TOTAL_LIFESPAN )
    {
      this.stop();
      return true;
    }

    return false;
  }

  private spawnSparks1(): void
  {
    const batch = Vfx3DRuntime.getBatch( VfxRadialStreakBatch );

    if ( VfxFireworkExplosionTask.sparks_1_gradient_id === -1 )
    {
      VfxFireworkExplosionTask.sparks_1_gradient_id = VfxGradientAtlas.registerGradient([
        { stop: 1,   color: VfxFireworkExplosionTask.SPARKS_1_COLOR_1 },
        { stop: 0.6, color: VfxFireworkExplosionTask.SPARKS_1_COLOR_2 },
      ]);
    }

    const count = VfxFireworkExplosionTask.SPARKS_1_BURST_COUNT;

    for ( let i = 0; i < count; i++ )
    {
      const angle = ( i / count ) * VfxFireworkExplosionTask.PI2 + randomRange( -0.1, 0.1 );
      const cosAngle = Math.cos( angle );
      const sinAngle = Math.sin( angle );
      const spawn_dist = randomRange(
        VfxFireworkExplosionTask.SPARKS_1_MIN_SPAWN_AREA_RADIUS,
        VfxFireworkExplosionTask.SPARKS_1_MAX_SPAWN_AREA_RADIUS
      );

      VfxFireworkExplosionTask.scratch_pos.set(
        this.position.x + cosAngle * spawn_dist,
        this.position.y + sinAngle * spawn_dist,
        this.position.z
      );

      VfxFireworkExplosionTask.scratch_vel.set(
        cosAngle * VfxFireworkExplosionTask.SPARKS_1_SPEED,
        sinAngle * VfxFireworkExplosionTask.SPARKS_1_SPEED,
        0.0
      );

      const width  = randomRange( VfxFireworkExplosionTask.SPARKS_1_MIN_WIDTH, VfxFireworkExplosionTask.SPARKS_1_MAX_WIDTH );
      const height = randomRange( VfxFireworkExplosionTask.SPARKS_1_MIN_HEIGHT, VfxFireworkExplosionTask.SPARKS_1_MAX_HEIGHT );
      const res_id = ResourceID.ParticleGlow1;

      batch.spawnParticle(
        res_id,
        VfxFireworkExplosionTask.sparks_1_gradient_id,
        VfxFireworkExplosionTask.scratch_pos,
        VfxFireworkExplosionTask.scratch_vel,
        width,
        height,
        VfxFireworkExplosionTask.SPARKS_1_BRIGHTNESS_FADE_START_PROGRESS,
        VfxFireworkExplosionTask.SPARKS_1_DISSOLVE_START_PROGRESS,
        this.start_time,
        VfxFireworkExplosionTask.SPARKS_1_LIFESPAN,
        1,
        VfxFireworkExplosionTask.SPARKS_1_DISSOLVE_DIRECTION_WEIGHT,
        VfxFireworkExplosionTask.SPARKS_1_DISSOLVE_SOFTNESS,
      );
    }
  }

  private spawnSparks2(): void
  {
    const batch = Vfx3DRuntime.getBatch( VfxBallisticStreakBatch );

    if ( VfxFireworkExplosionTask.sparks_2_gradient_id === -1 )
    {
      VfxFireworkExplosionTask.sparks_2_gradient_id = VfxGradientAtlas.registerGradient([
        { stop: 0.0, color: VfxFireworkExplosionTask.SPARKS_2_COLOR_1 },
        { stop: VfxFireworkExplosionTask.SPARKS_2_COLOR_2_PROGRESS, color: VfxFireworkExplosionTask.SPARKS_2_COLOR_2 },
        { stop: VfxFireworkExplosionTask.SPARKS_2_COLOR_3_PROGRESS, color: VfxFireworkExplosionTask.SPARKS_2_COLOR_3 },
        { stop: 1.0, color: new THREE.Color( 0x000000 ) }
      ]);
    }

    const count = VfxFireworkExplosionTask.SPARKS_2_BURST_COUNT;

    for ( let i = 0; i < count; i++ )
    {
      const angle = ( i / count ) * VfxFireworkExplosionTask.PI2 + randomRange( -0.2, 0.2 );
      const cosAngle = Math.cos( angle );
      const sinAngle = Math.sin( angle );
      const spawn_dist = randomRange( 0, VfxFireworkExplosionTask.SPARKS_2_SPAWN_AREA_RADIUS );
      
      VfxFireworkExplosionTask.scratch_pos.set(
        this.position.x + cosAngle * spawn_dist,
        this.position.y + sinAngle * spawn_dist,
        this.position.z + 0.01
      );

      const speed = randomRange(
        VfxFireworkExplosionTask.SPARKS_2_MIN_SPEED,
        VfxFireworkExplosionTask.SPARKS_2_MAX_SPEED
      );
      const up_speed = randomRange(
        VfxFireworkExplosionTask.SPARKS_2_UP_MIN,
        VfxFireworkExplosionTask.SPARKS_2_UP_MAX
      );
      VfxFireworkExplosionTask.scratch_vel.set(
        cosAngle * speed,
        sinAngle * speed + up_speed,
        0.0
      );

      const width = randomRange(
        VfxFireworkExplosionTask.SPARKS_2_MIN_WIDTH,
        VfxFireworkExplosionTask.SPARKS_2_MAX_WIDTH
      );
      const height = randomRange(
        VfxFireworkExplosionTask.SPARKS_2_MIN_HEIGHT,
        VfxFireworkExplosionTask.SPARKS_2_MAX_HEIGHT
      );
      VfxFireworkExplosionTask.scratch_size.set( width, height );

      batch.spawnParticle(
        ResourceID.EllipseGlow1,
        VfxFireworkExplosionTask.sparks_2_gradient_id,
        VfxFireworkExplosionTask.scratch_pos,
        VfxFireworkExplosionTask.scratch_vel,
        VfxFireworkExplosionTask.SPARKS_2_GRAVITY,
        VfxFireworkExplosionTask.scratch_size,
        VfxFireworkExplosionTask.SPARKS_2_STRETCH_FACTOR,
        VfxFireworkExplosionTask.SPARKS_2_SHRINK_START_RATIO,
        this.start_time,
        VfxFireworkExplosionTask.SPARKS_2_LIFESPAN
      );
    }
  }

  private spawnSparks3(): void
  {
    const batch = Vfx3DRuntime.getBatch( VfxRadialStreakBatch );

    if ( VfxFireworkExplosionTask.sparks_3_gradient_id === -1 )
    {
      VfxFireworkExplosionTask.sparks_3_gradient_id = VfxGradientAtlas.registerGradient([
        { stop: 1.0, color: VfxFireworkExplosionTask.SPARKS_3_COLOR_1 },
        { stop: 0.5, color: VfxFireworkExplosionTask.SPARKS_3_COLOR_2 },
        { stop: 0.1, color: VfxFireworkExplosionTask.SPARKS_3_COLOR_3 },
      ]);
    }

    const count = VfxFireworkExplosionTask.SPARKS_3_BURST_COUNT;

    for ( let i = 0; i < count; i++ )
    {
      const angle = ( ( i + 0.5 ) / count ) * VfxFireworkExplosionTask.PI2 + randomRange( -0.15, 0.15 );
      const cosAngle = Math.cos( angle );
      const sinAngle = Math.sin( angle );
      const spawn_dist = randomRange(
        VfxFireworkExplosionTask.SPARKS_3_MIN_SPAWN_AREA_RADIUS,
        VfxFireworkExplosionTask.SPARKS_3_MAX_SPAWN_AREA_RADIUS
      );

      VfxFireworkExplosionTask.scratch_pos.set(
        this.position.x + cosAngle * spawn_dist,
        this.position.y + sinAngle * spawn_dist,
        this.position.z
      );

      VfxFireworkExplosionTask.scratch_vel.set(
        cosAngle * VfxFireworkExplosionTask.SPARKS_3_SPEED,
        sinAngle * VfxFireworkExplosionTask.SPARKS_3_SPEED,
        0.0
      );

      const width  = randomRange( VfxFireworkExplosionTask.SPARKS_3_MIN_WIDTH, VfxFireworkExplosionTask.SPARKS_3_MAX_WIDTH );
      const height = randomRange( VfxFireworkExplosionTask.SPARKS_3_MIN_HEIGHT, VfxFireworkExplosionTask.SPARKS_3_MAX_HEIGHT );
      const res_id = ResourceID.SparkGlow4;

      batch.spawnParticle(
        res_id,
        VfxFireworkExplosionTask.sparks_3_gradient_id,
        VfxFireworkExplosionTask.scratch_pos,
        VfxFireworkExplosionTask.scratch_vel,
        width,
        height,
        VfxFireworkExplosionTask.SPARKS_3_BRIGHTNESS_FADE_START_PROGRESS,
        VfxFireworkExplosionTask.SPARKS_3_DISSOLVE_START_PROGRESS,
        this.start_time,
        VfxFireworkExplosionTask.SPARKS_3_LIFESPAN,
        1,
        VfxFireworkExplosionTask.SPARKS_3_DISSOLVE_DIRECTION_WEIGHT,
        VfxFireworkExplosionTask.SPARKS_3_DISSOLVE_SOFTNESS,
      );
    }
  }
}
